import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Paperclip, Search, Users } from "lucide-react";

import { ListPager } from "@/components/ListPager";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { usePage } from "@/hooks/usePage";
import { ApiError, fetchSignedAsset } from "@/lib/api";
import { addDaysIso, todayIso } from "@/lib/date-range";
import { browserTimeZone, formatDateIn, formatDateTimeIn, formatTimeIn } from "@/lib/datetime";
import {
  employeeApi,
  type RequestStatus,
  type TeamDecision,
  type TeamDayRow,
  type TeamDayStatus,
  type TeamMember,
  type TeamRequestDetail,
  type TeamRequestRow,
  type TeamSummary,
  type WorkMode,
} from "@/lib/employee-api";
import { displayTypeName, requestStatusLabel, requestStatusVariant } from "@/lib/request-labels";
import { formatDuration } from "@/lib/work-hours";

/**
 * My team (FEAT-2026-026, ADR-0018): a superior's view of their direct
 * reports — shown in the lane only to someone who has them (BR-206). Times
 * and status, never place (BR-207). When the tenant turns on line-manager
 * approval (FEAT-2026-027, BR-211, ADR-0019) the manager also opens their
 * reports' requests — with attachments and site names — and decides them;
 * otherwise nothing here approves or changes anything.
 */

const PAGE_SIZE = 50;
/** The server answers for today back to today − 62 days. */
const WINDOW_DAYS = 62;
const PERSON_RANGES = [14, 31, 62] as const;

const STATUS_LABEL: Record<TeamDayStatus, string> = {
  CHECKED_IN: "Checked in",
  CHECKED_OUT: "Checked out",
  PARTIAL: "Partial day",
  ON_LEAVE: "On leave",
  NOT_WORKING: "Not working",
  NO_CHECK_IN: "No check-in",
  NOT_STARTED: "Not started yet",
};

const STATUS_TONE: Record<TeamDayStatus, string> = {
  CHECKED_IN: "border-success/30 bg-success/5 text-success",
  CHECKED_OUT: "border-primary/30 bg-primary/5 text-primary",
  PARTIAL: "border-warning/30 bg-warning/5 text-warning",
  ON_LEAVE: "border-info/30 bg-info/5 text-info",
  NOT_WORKING: "border-border text-muted-foreground",
  NO_CHECK_IN: "border-destructive/30 bg-destructive/5 text-destructive",
  NOT_STARTED: "border-border text-muted-foreground",
};

const MODE_LABEL: Record<WorkMode, string> = { WFO: "Office", WFH: "Home", WFA: "Anywhere" };

const REQUEST_STATUSES: RequestStatus[] = ["PENDING", "APPROVED", "REJECTED", "NEEDS_CLARIFICATION", "CANCELLED"];

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

function StatusBadge({ status }: { status: TeamDayStatus }) {
  return (
    <Badge variant="outline" className={`whitespace-nowrap font-normal ${STATUS_TONE[status]}`}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

const hours = (r: TeamDayRow) => (r.workedMinutes != null ? formatDuration(r.workedMinutes) : "—");

export default function MyTeam() {
  const [tz, setTz] = useState<string | null>(null);
  const [approval, setApproval] = useState(false);
  const [person, setPerson] = useState<TeamMember | null>(null);
  // `?request=<id>` opens that request: the manager's notifications link here.
  const [params, setParams] = useSearchParams();
  const requestId = params.get("request");
  const [tab, setTab] = useState(requestId ? "requests" : "day");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    employeeApi
      .config()
      .then((c) => {
        setTz(c.tenant.timezone);
        setApproval(c.requests?.lineManagerApprovalEnabled === true);
      })
      .catch(() => setTz(browserTimeZone())); // non-fatal — the browser's zone is the right answer for local staff
  }, []);

  const openRequest = useCallback(
    (id: string | null) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (id) next.set("request", id);
          else next.delete("request");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  if (!tz) return null;
  const today = todayIso(tz);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-semibold">My team</h1>
        <p className="text-sm text-muted-foreground">
          The people who report to you: their check-in times, status and requests.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="day">Day</TabsTrigger>
          <TabsTrigger value="requests">Requests</TabsTrigger>
        </TabsList>
        <TabsContent value="day">
          <TeamDay tz={tz} today={today} onOpen={setPerson} />
        </TabsContent>
        <TabsContent value="requests">
          <TeamRequests tz={tz} approval={approval} refresh={refresh} onOpen={openRequest} />
        </TabsContent>
      </Tabs>
      <PersonSheet tz={tz} today={today} person={person} onClose={() => setPerson(null)} />
      {approval && (
        <TeamRequestSheet
          tz={tz}
          requestId={requestId}
          onClose={() => openRequest(null)}
          onDecided={() => setRefresh((n) => n + 1)}
        />
      )}
    </div>
  );
}

function TeamDay({ tz, today, onOpen }: { tz: string; today: string; onOpen: (m: TeamMember) => void }) {
  const [date, setDate] = useState(today);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const filters = useMemo(() => ({ date, search: debouncedSearch || undefined }), [date, debouncedSearch]);
  // Any filter change goes back to page 1.
  const [page, setPage] = usePage(JSON.stringify(filters));
  const [rows, setRows] = useState<TeamDayRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<TeamSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    employeeApi.team
      .day({ ...filters, page, pageSize: PAGE_SIZE })
      .then((r) => {
        if (cancelled) return;
        setRows(r.data);
        setTotal(r.total);
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(errorText(e, "Could not load your team")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page]);

  // The counts are about the whole team, whatever page or search is on screen.
  useEffect(() => {
    let cancelled = false;
    employeeApi.team
      .summary(date)
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { if (!cancelled) setSummary(null); /* non-fatal — the table reports errors */ });
    return () => { cancelled = true; };
  }, [date]);

  const counts: Array<[string, number | undefined]> = [
    ["Team", summary?.teamSize],
    ["Checked in", summary?.checkedIn],
    ["Checked out", summary?.checkedOut],
    ["On leave", summary?.onLeave],
    ["Not working", summary?.notWorking],
    ["No check-in", summary?.noCheckIn],
  ];

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Team counts" className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {counts.map(([label, value]) => (
          <Card key={label} className="p-3">
            <div className="text-[11px] text-muted-foreground">{label}</div>
            <div className="mt-0.5 text-lg font-semibold">{value ?? "—"}</div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="team-day" className="text-xs text-muted-foreground">Day</Label>
          <Input
            id="team-day"
            type="date"
            className="mt-1 h-9 w-40"
            value={date}
            min={addDaysIso(today, -WINDOW_DAYS)}
            max={today}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <Label htmlFor="team-search" className="text-xs text-muted-foreground">Search</Label>
          <div className="relative mt-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="team-search"
              type="search"
              className="h-9 pl-9"
              placeholder="Search name or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-2.5 font-medium">Name</th>
                  <th className="p-2.5 font-medium">Department</th>
                  <th className="p-2.5 font-medium">In</th>
                  <th className="p-2.5 font-medium">Out</th>
                  <th className="p-2.5 font-medium">Hours</th>
                  <th className="p-2.5 font-medium">Status</th>
                  <th className="p-2.5 font-medium">Mode</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      <Users className="mx-auto mb-2 h-6 w-6 opacity-40" />
                      Nobody matches your search.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.employeeId}
                    className="cursor-pointer border-b last:border-0 hover:bg-secondary/30"
                    onClick={() => onOpen(r)}
                  >
                    <td className="p-2.5">
                      {/* The row is clickable for a mouse; the name is the keyboard and screen-reader way in. */}
                      <button
                        type="button"
                        className="text-left font-medium leading-tight hover:underline focus-visible:underline focus-visible:outline-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(r);
                        }}
                      >
                        {r.name}
                      </button>
                      <div className="text-xs text-muted-foreground">{r.empCode}</div>
                    </td>
                    <td className="p-2.5 text-xs text-muted-foreground">{r.departmentPath ?? "—"}</td>
                    <td className="p-2.5 font-mono text-xs">{formatTimeIn(r.checkInAt, tz)}</td>
                    <td className="p-2.5 font-mono text-xs">{formatTimeIn(r.checkOutAt, tz)}</td>
                    <td className="p-2.5 text-xs">{hours(r)}</td>
                    <td className="p-2.5"><StatusBadge status={r.dayStatus} /></td>
                    <td className="p-2.5 text-xs">{r.workMode ? MODE_LABEL[r.workMode] : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPager page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} noun="people" />
        </Card>
      )}
    </div>
  );
}

function TeamRequests({
  tz, approval, refresh, onOpen,
}: { tz: string; approval: boolean; refresh: number; onOpen: (id: string) => void }) {
  // "Waiting for you" (FEAT-2026-027) — pending or needs clarification — is the
  // default while there is something to decide.
  const [status, setStatus] = useState<"waiting" | "all" | RequestStatus>(approval ? "waiting" : "all");
  const [waitingCount, setWaitingCount] = useState<number | null>(null);
  const filters = useMemo(
    () =>
      status === "waiting"
        ? { waiting: true }
        : { status: status === "all" ? undefined : status },
    [status],
  );
  const [page, setPage] = usePage(JSON.stringify(filters));
  const [rows, setRows] = useState<TeamRequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    employeeApi.team
      .requests({ ...filters, page, pageSize: PAGE_SIZE })
      .then((r) => {
        if (cancelled) return;
        setRows(r.data);
        setTotal(r.total);
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(errorText(e, "Could not load your team's requests")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, refresh]);

  useEffect(() => {
    if (!approval) return;
    let cancelled = false;
    employeeApi.team
      .requests({ waiting: true, page: 1, pageSize: 1 })
      .then((r) => {
        if (cancelled) return;
        setWaitingCount(r.total);
        if (r.total === 0) setStatus((s) => (s === "waiting" ? "all" : s));
      })
      .catch(() => { /* the count is a convenience; the list still works */ });
    return () => { cancelled = true; };
  }, [approval, refresh]);

  const dates = (r: TeamRequestRow) =>
    r.dateTo && r.dateTo !== r.date ? `${formatDateIn(r.date, tz)} – ${formatDateIn(r.dateTo, tz)}` : formatDateIn(r.date, tz);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {approval && <SelectItem value="waiting">Waiting for you</SelectItem>}
              <SelectItem value="all">All statuses</SelectItem>
              {REQUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{requestStatusLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {approval && waitingCount !== null && (
          <Button
            size="sm"
            variant={status === "waiting" ? "default" : "outline"}
            onClick={() => setStatus("waiting")}
          >
            {`Waiting for you · ${waitingCount}`}
          </Button>
        )}
      </div>
      {error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-2.5 font-medium">Employee</th>
                  <th className="p-2.5 font-medium">Type</th>
                  <th className="p-2.5 font-medium">Dates</th>
                  <th className="p-2.5 font-medium">Status</th>
                  <th className="p-2.5 font-medium">Submitted</th>
                  <th className="p-2.5 font-medium">Decided</th>
                  <th className="p-2.5 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No requests.</td></tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b last:border-0 ${approval ? "cursor-pointer hover:bg-secondary/30" : ""}`}
                    onClick={approval ? () => onOpen(r.id) : undefined}
                  >
                    <td className="p-2.5">
                      {approval ? (
                        <button
                          type="button"
                          className="text-left font-medium leading-tight hover:underline"
                          onClick={(e) => { e.stopPropagation(); onOpen(r.id); }}
                        >
                          {r.name}
                        </button>
                      ) : (
                        <div className="font-medium leading-tight">{r.name}</div>
                      )}
                      <div className="text-xs text-muted-foreground">{r.empCode}</div>
                    </td>
                    <td className="p-2.5 text-xs">{displayTypeName(r.type, r.typeLabel)}</td>
                    <td className="p-2.5 text-xs whitespace-nowrap">{dates(r)}</td>
                    <td className="p-2.5"><Badge variant={requestStatusVariant(r.status)}>{requestStatusLabel(r.status)}</Badge></td>
                    <td className="p-2.5 text-xs text-muted-foreground">{formatDateTimeIn(r.createdAt, tz)}</td>
                    <td className="p-2.5 text-xs text-muted-foreground">{r.decidedAt ? formatDateTimeIn(r.decidedAt, tz) : "—"}</td>
                    <td className="max-w-[16rem] p-2.5 text-xs">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPager page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} noun="requests" />
        </Card>
      )}
    </div>
  );
}

function PersonSheet({
  tz, today, person, onClose,
}: { tz: string; today: string; person: TeamMember | null; onClose: () => void }) {
  const [range, setRange] = useState<(typeof PERSON_RANGES)[number]>(14);
  const [days, setDays] = useState<TeamDayRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!person) return;
    let cancelled = false;
    setDays(null);
    setError(null);
    employeeApi.team
      .memberDays(person.employeeId, { from: addDaysIso(today, -(range - 1)), to: today })
      .then((r) => { if (!cancelled) setDays([...r.days].reverse()); })
      .catch((e) => { if (!cancelled) setError(errorText(e, "Could not load their days")); });
    return () => { cancelled = true; };
  }, [person, range, today]);

  return (
    <Sheet open={!!person} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {person && (
          <>
            <SheetHeader>
              <SheetTitle className="text-left">{person.name}</SheetTitle>
              <SheetDescription className="text-left">
                {person.empCode}{person.role ? ` · ${person.role}` : ""}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 w-40">
              <Label className="text-xs text-muted-foreground">Showing</Label>
              <Select value={String(range)} onValueChange={(v) => setRange(Number(v) as typeof range)}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERSON_RANGES.map((n) => <SelectItem key={n} value={String(n)}>Last {n} days</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
            {days && (
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium">In</th>
                    <th className="py-2 font-medium">Out</th>
                    <th className="py-2 font-medium">Hours</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date} className="border-b last:border-0">
                      <td className="py-2 text-xs">{formatDateIn(d.date, tz)}</td>
                      <td className="py-2 font-mono text-xs">{formatTimeIn(d.checkInAt, tz)}</td>
                      <td className="py-2 font-mono text-xs">{formatTimeIn(d.checkOutAt, tz)}</td>
                      <td className="py-2 text-xs">{hours(d)}</td>
                      <td className="py-2"><StatusBadge status={d.dayStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const DECISION_VERB: Partial<Record<RequestStatus, string>> = {
  APPROVED: "Approved",
  REJECTED: "Rejected",
  NEEDS_CLARIFICATION: "Sent back",
};

/**
 * One direct report's request, for their line manager to decide (BR-211).
 * Content, attachments and the named sites it mentions — never place. Once
 * decided, only HR can change it, so a decided request shows who decided and
 * no buttons.
 */
function TeamRequestSheet({
  tz, requestId, onClose, onDecided,
}: { tz: string; requestId: string | null; onClose: () => void; onDecided: () => void }) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<TeamRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState<TeamDecision | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!requestId) return;
    let cancelled = false;
    setError(null);
    employeeApi.team
      .request(requestId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(errorText(e, "Could not load this request")); });
    return () => { cancelled = true; };
  }, [requestId, reload]);

  useEffect(() => {
    if (!requestId) {
      setDetail(null);
      setNote("");
    }
  }, [requestId]);

  const decide = async (decision: TeamDecision) => {
    if (!detail) return;
    setActing(decision);
    try {
      await employeeApi.team.decide(detail.id, decision, note.trim() || undefined);
      toast({ title: decision === "approve" ? "Request approved" : decision === "reject" ? "Request rejected" : "Sent back for clarification" });
      onDecided();
      onClose();
    } catch (e) {
      // ALREADY_DECIDED: HR got there first. Say so, and show what stands now.
      toast({ variant: "destructive", title: "Could not save your decision", description: errorText(e, "Something went wrong") });
      setReload((n) => n + 1);
      onDecided();
    } finally {
      setActing(null);
    }
  };

  const openAttachment = async (url: string) => {
    const tab = window.open("", "_blank");
    try {
      const objectUrl = await fetchSignedAsset(url);
      if (tab) tab.location.href = objectUrl;
      else window.open(objectUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      tab?.close();
      toast({ variant: "destructive", title: errorText(e, "Could not open the attachment") });
    }
  };

  const dates = (d: TeamRequestDetail) =>
    d.dateTo && d.dateTo !== d.date ? `${formatDateIn(d.date, tz)} – ${formatDateIn(d.dateTo, tz)}` : formatDateIn(d.date, tz);
  const sites = detail
    ? [
        detail.metadata?.correctedSite?.siteName && { label: "Correct site", value: detail.metadata.correctedSite.siteName },
        detail.metadata?.recordedState?.siteName && { label: "Recorded site", value: detail.metadata.recordedState.siteName },
      ].filter((x): x is { label: string; value: string } => !!x)
    : [];
  const ot = detail?.metadata?.overtime;

  return (
    <Sheet open={!!requestId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {error && <p className="mt-6 text-sm text-destructive">{error}</p>}
        {detail && (
          <>
            <SheetHeader>
              <SheetTitle className="text-left">{`${displayTypeName(detail.type, detail.typeLabel)} · ${detail.name}`}</SheetTitle>
              <SheetDescription className="text-left">
                {detail.empCode} · <Badge variant={requestStatusVariant(detail.status)}>{requestStatusLabel(detail.status)}</Badge>
              </SheetDescription>
            </SheetHeader>
            <dl className="mt-4 space-y-3 text-sm">
              <div><dt className="text-xs text-muted-foreground">Dates</dt><dd>{dates(detail)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Asking for</dt><dd>{detail.requestedChange}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Reason</dt><dd>{detail.reason}</dd></div>
              {ot?.startTime && ot.endTime && (
                <div><dt className="text-xs text-muted-foreground">Overtime</dt><dd>{`${ot.startTime} – ${ot.endTime}`}</dd></div>
              )}
              {sites.map((s) => (
                <div key={s.label}><dt className="text-xs text-muted-foreground">{s.label}</dt><dd>{s.value}</dd></div>
              ))}
              <div>
                <dt className="text-xs text-muted-foreground">Attachments</dt>
                <dd className="space-y-1">
                  {detail.attachments.length === 0 && <span className="text-muted-foreground">None</span>}
                  {detail.attachments.map((a) => (
                    <button
                      key={a.url}
                      type="button"
                      className="flex items-center gap-1.5 text-primary hover:underline"
                      onClick={() => void openAttachment(a.url)}
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      {a.filename}
                    </button>
                  ))}
                </dd>
              </div>
            </dl>

            {detail.canDecide ? (
              <div className="mt-6 space-y-3">
                <Label htmlFor="team-decision-note" className="text-xs">{`Note to ${detail.name} (optional)`}</Label>
                <Textarea
                  id="team-decision-note"
                  rows={2}
                  maxLength={1000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void decide("approve")} disabled={acting !== null}>Approve</Button>
                  <Button variant="outline" onClick={() => void decide("reject")} disabled={acting !== null}>Reject</Button>
                  <Button variant="ghost" onClick={() => void decide("needs-clarification")} disabled={acting !== null}>
                    Needs clarification
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Your decision is final. HR can still change it.</p>
              </div>
            ) : (
              detail.decidedByName && (
                <div className="mt-6 rounded-lg border bg-muted/30 p-3 text-sm">
                  <div className="font-medium">
                    {`${DECISION_VERB[detail.status] ?? "Decided"} by ${detail.decidedByName} (${detail.decidedByRole === "LINE_MANAGER" ? "line manager" : "HR"})`}
                  </div>
                  {detail.decidedAt && (
                    <div className="text-xs text-muted-foreground">{formatDateTimeIn(detail.decidedAt, tz)}</div>
                  )}
                  {detail.decisionNote && <div className="mt-1 text-xs">{detail.decisionNote}</div>}
                </div>
              )
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

